(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const optionalNames = ['contact[Request type]', 'contact[Order number]', 'contact[Studio]', 'contact[Rug reference]', 'contact[Project location]', 'contact[Target timing]'];
  function enhanceSelect(select) {
    if (select.dataset.selectReady || select.multiple) return;
    const field = select.closest('.vr-field');
    const label = field?.querySelector('label');
    if (!field || !label) return;
    const root = document.createElement('div');
    root.className = 'vr-select';
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.id = select.id + '-trigger';
    trigger.className = 'vr-select-trigger';
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    const value = document.createElement('span');
    value.className = 'vr-select-value';
    value.id = select.id + '-value';
    const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    arrow.setAttribute('viewBox', '0 0 16 10');
    arrow.setAttribute('aria-hidden', 'true');
    arrow.innerHTML = '<path d="m2 2 6 6 6-6" fill="none" stroke="currentColor" stroke-width="1.25"/>';
    trigger.append(value, arrow);
    const list = document.createElement('div');
    list.className = 'vr-select-list';
    list.id = select.id + '-list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;
    label.id ||= select.id + '-label';
    trigger.setAttribute('aria-labelledby', label.id + ' ' + value.id);
    trigger.setAttribute('aria-controls', list.id);
    list.setAttribute('aria-labelledby', label.id);
    root.append(trigger, list);
    select.after(root);
    let options = [], active = 0, open = false, search = '', searchAt = 0;
    function sync() {
      value.textContent = select.selectedOptions[0]?.textContent || '';
      trigger.disabled = select.disabled;
      options = [...select.options].filter(option => (!option.hidden || option.selected) && !option.disabled);
      active = Math.max(0, options.indexOf(select.selectedOptions[0]));
      list.replaceChildren(...options.map((option, index) => {
        const row = document.createElement('div');
        row.id = select.id + '-option-' + index;
        row.className = 'vr-select-option';
        row.setAttribute('role', 'option');
        row.setAttribute('aria-selected', String(option.selected));
        row.textContent = option.textContent;
        row.addEventListener('pointerdown', event => event.preventDefault());
        row.addEventListener('click', () => { active = index; close(true); trigger.focus({ preventScroll: true }); });
        return row;
      }));
    }
    function highlight(index) {
      active = Math.min(options.length - 1, Math.max(0, index));
      [...list.children].forEach((row, i) => {
        row.classList.toggle('is-active', i === active);
        row.setAttribute('aria-selected', String(i === active));
      });
      const row = list.children[active];
      if (row) {
        trigger.setAttribute('aria-activedescendant', row.id);
        if (row.offsetTop < list.scrollTop) list.scrollTop = row.offsetTop;
        else if (row.offsetTop + row.offsetHeight > list.scrollTop + list.clientHeight)
          list.scrollTop = row.offsetTop + row.offsetHeight - list.clientHeight;
      }
    }
    function show() {
      if (open || trigger.disabled) return;
      sync();
      const bounds = trigger.getBoundingClientRect();
      const below = window.innerHeight - bounds.bottom - 16;
      const above = bounds.top - 16;
      const up = below < 230 && above > below;
      root.classList.toggle('opens-up', up);
      root.style.setProperty('--vr-select-space', Math.max(96, Math.min(320, up ? above : below)) + 'px');
      open = true;
      root.classList.add('is-open');
      list.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      highlight(active);
    }
    function close(commit = false) {
      if (!open) return;
      open = false;
      root.classList.remove('is-open');
      list.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      trigger.removeAttribute('aria-activedescendant');
      if (commit && options[active] && select.value !== options[active].value) {
        select.value = options[active].value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      sync();
    }
    trigger.addEventListener('click', () => open ? close() : show());
    trigger.addEventListener('keydown', event => {
      const key = event.key;
      if (key === 'Tab') { close(true); return; }
      if (key === 'Escape') { event.preventDefault(); close(); return; }
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp', 'Home', 'End', 'PageDown', 'PageUp'].includes(key)) {
        event.preventDefault();
        if (open && ['Enter', ' '].includes(key)) { close(true); return; }
        if (open && event.altKey && key === 'ArrowUp') { close(true); return; }
        const wasOpen = open; show();
        if (key === 'Home') highlight(0);
        else if (key === 'End') highlight(options.length - 1);
        else if (wasOpen && key === 'ArrowDown') highlight(active + 1);
        else if (wasOpen && key === 'ArrowUp') highlight(active - 1);
        else if (key === 'PageDown') highlight(active + 10);
        else if (key === 'PageUp') highlight(active - 10);
        return;
      }
      if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault(); show();
        const now = performance.now();
        search = now - searchAt > 700 ? key : search + key;
        searchAt = now;
        const repeating = [...search].every(char => char.toLowerCase() === key.toLowerCase());
        const match = repeating ? key.toLowerCase() : search.toLowerCase();
        const start = repeating ? active + 1 : 0;
        for (let offset = 0; offset < options.length; offset++) {
          const index = (start + offset) % options.length;
          if (options[index].textContent.trim().toLowerCase().startsWith(match)) { highlight(index); break; }
        }
      }
    });
    trigger.addEventListener('blur', () => close(true));
    const outside = event => { if (open && !root.contains(event.target)) close(true); };
    document.addEventListener('pointerdown', outside);
    select.addEventListener('change', sync);
    select.form?.addEventListener('reset', () => window.setTimeout(() => {
      close(); sync(); select.dispatchEvent(new Event('change', { bubbles: true }));
    }, 0));
    sync();
    label.htmlFor = trigger.id;
    select.hidden = true;
    select.dataset.selectReady = 'true';
    const teardown = event => {
      if (event.target.contains(select)) {
        document.removeEventListener('pointerdown', outside);
        document.removeEventListener('shopify:section:unload', teardown);
      }
    };
    document.addEventListener('shopify:section:unload', teardown);
  }

  function init(scope = document) {
    const pages = [...scope.querySelectorAll('[data-vorug-service]')];
    if (scope.matches?.('[data-vorug-service]')) pages.unshift(scope);
    pages.forEach(page => {
      if (page.dataset.serviceReady) return;
      page.dataset.serviceReady = 'true';
      page.querySelectorAll('form.contact-form').forEach(form => {
      const purpose = form.querySelector('[data-enquiry-purpose]');
      const message = form.querySelector('textarea[name="contact[body]"]');
      const params = new URLSearchParams(location.search);
      const storageKey = `vorug-enquiry:${form.id}`;
      const statusBelongsHere = !location.hash || location.hash === `#${form.id}`;
      if (!statusBelongsHere) form.querySelectorAll('[data-enquiry-success], [data-enquiry-errors]').forEach(notice => { notice.hidden = true; });
      const success = statusBelongsHere ? form.querySelector('[data-enquiry-success]') : null;
      const errors = statusBelongsHere ? form.querySelector('[data-enquiry-errors]') : null;
      const returnTo = form.querySelector('input[name="return_to"]');
      const returnParams = new URLSearchParams(location.search);
      returnParams.set('contact_posted', 'true');
      if (returnTo) returnTo.value = `${location.pathname}?${returnParams}#${form.id}`;
      try {
        if (success) sessionStorage.removeItem(storageKey);
        else if (errors) {
          const previous = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
          optionalNames.forEach(name => {
            const control = [...form.elements].find(field => field.name === name);
            if (control && typeof previous[name] === 'string') control.value = previous[name];
          });
        }
      } catch {}
      const projectDetails = form.querySelector('[data-project-details]');
      if (projectDetails && [...projectDetails.querySelectorAll('input')].some(input => input.value.trim())) projectDetails.open = true;
      function currentIntent() { return purpose?.selectedOptions[0]?.dataset.intent || 'other'; }
      function updateFields(animate = false) {
        const intent = currentIntent();
        form.querySelectorAll('[data-enquiry-field]').forEach(field => {
          const show = field.dataset.enquiryField.split(' ').includes(intent);
          const wasHidden = field.hidden;
          field.hidden = !show;
          field.querySelectorAll('input').forEach(input => { input.disabled = !show; });
          if (show && wasHidden && animate && !reducedMotion.matches && field.animate) {
            field.animate([{ backgroundColor: '#f1eee8' }, { backgroundColor: 'transparent' }], { duration: 180, easing: 'ease-out' });
          }
        });
      }
      if (purpose) {
        const requested = params.get('request');
        const option = [...purpose.options].find(item => item.dataset.intent === requested);
        if (option && !errors) purpose.value = option.value;
        if (requested === 'terms') {
          page.querySelector('h1').textContent = page.dataset.termsTitle;
          if (!message.value && !success) message.value = page.dataset.termsMessage;
        }
        updateFields();
        purpose.addEventListener('change', () => updateFields(true));
        const title = params.get('title') || '';
        const size = params.get('size') || '';
        if (['sample', 'advice'].includes(requested)) {
          page.querySelector('h1').textContent = requested === 'sample' ? page.dataset.sampleTitle : page.dataset.adviceTitle;
          if (title && size) {
            const context = document.createElement('div');
            context.className = 'vr-context';
            const label = document.createElement('p');
            label.textContent = requested === 'sample' ? page.dataset.contextSample : page.dataset.contextAdvice;
            const detail = document.createElement('p');
            const price = params.get('price') || '';
            detail.textContent = [title, size, price].filter(Boolean).join(' · ');
            context.append(label, detail);
            let safeReturn = '';
            try {
              const url = new URL(params.get('return') || '', location.origin);
              if (params.get('return') && url.origin === location.origin && /^\/products\//.test(url.pathname)) safeReturn = url.pathname + url.search + url.hash;
            } catch {}
            if (safeReturn) {
              const back = document.createElement('a'); back.href = safeReturn; back.textContent = page.dataset.backLabel; context.appendChild(back);
            }
            form.before(context);
            if (!message.value && !success) {
              message.value = page.dataset.contextMessage.replace('__RUG__', `${title} (${size}${price ? `, ${price}` : ''})`).replace('__PURPOSE__', requested === 'sample' ? page.dataset.contextSample.toLowerCase() : page.dataset.contextAdvice.toLowerCase());
            }
            const fields = { 'contact[Product]': title, 'contact[Product handle]': params.get('product'), 'contact[Variant]': `${size}${params.get('variant') ? ` (variant ${params.get('variant')})` : ''}`, 'contact[Price]': price, 'contact[Return URL]': safeReturn };
            Object.entries(fields).forEach(([name, value]) => {
              if (!value) return;
              const input = document.createElement('input'); input.type = 'hidden'; input.name = name; input.value = value; form.appendChild(input);
            });
          }
        }
      }
      form.querySelectorAll('.vr-field select').forEach(enhanceSelect);
      const button = form.querySelector('button[type="submit"]');
      const buttonContents = button.innerHTML;
      function resetButton() { button.disabled = false; button.innerHTML = buttonContents; form.removeAttribute('aria-busy'); }
      form.addEventListener('submit', event => {
        if (!form.checkValidity()) return;
        try {
          const snapshot = {};
          optionalNames.forEach(name => {
            const field = [...form.elements].find(item => item.name === name);
            if (field) snapshot[name] = field.value;
          });
          sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
        } catch {}
        queueMicrotask(() => {
          if (event.defaultPrevented) return;
          button.disabled = true; button.textContent = button.dataset.sendingLabel; form.setAttribute('aria-busy', 'true');
          window.setTimeout(resetButton, 10000);
        });
      });
      window.addEventListener('pageshow', resetButton);
      if (success || errors) (success || errors).focus();
      });
      if (page.dataset.vorugService === 'trade') {
        page.querySelectorAll('a[href="#project-enquiry"], a[href="#project-enquiry-quick"]').forEach(jump => jump.addEventListener('click', event => {
          const anchor = jump.getAttribute('href');
          const target = page.querySelector(anchor);
          if (!target) return;
          event.preventDefault();
          history.replaceState(null, '', anchor);
          target.scrollIntoView({ behavior: reducedMotion.matches ? 'instant' : 'smooth', block: 'start' });
          const heading = target.querySelector('h2'); heading.tabIndex = -1; heading.focus({ preventScroll: true });
        }));
        const photo = page.querySelector('[data-service-photo] img');
        if (!reducedMotion.matches && photo?.animate) {
          const animation = photo.animate([{ transform: 'scale(1.025)' }, { transform: 'scale(1)' }], { duration: 650, easing: 'cubic-bezier(.16,1,.3,1)' });
          reducedMotion.addEventListener('change', () => { if (reducedMotion.matches) animation.finish(); }, { once: true });
        }
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();

